(() => {
  const checkoutSelector = 'button[name="checkout"], input[name="checkout"]';
  const methodLabels = {
    delivery: 'local delivery',
    pickup: 'store pickup'
  };

  let config = {
    enabled: false,
    estimatorEnabled: false,
    cartTotal: 0,
    minimum: 0,
    moneyFormat: '${{amount}}'
  };
  let fulfillmentMethod = null;
  let observerQueued = false;
  let lastPostalCode = '';
  let rateRequest = 0;

  const normalizedText = element => (element?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const isVisible = element => Boolean(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));

  const interactiveControl = element => {
    let current = element;
    for (let depth = 0; current && depth < 7; depth++, current = current.parentElement) {
      if (current.matches?.('button, [role="button"], [role="tab"], label, a')) return current;
      const text = normalizedText(current);
      if (text === methodLabels.delivery || text === methodLabels.pickup || text === 'check') return current;
    }
    return element;
  };

  const findTextControl = label => {
    const elements = document.querySelectorAll('button, [role="button"], [role="tab"], label, a, div, span');
    return Array.from(elements).find(element => isVisible(element) && normalizedText(element) === label);
  };

  const isSelected = element => {
    if (!element) return false;
    const control = interactiveControl(element);
    const input = control.matches?.('label')
      ? document.getElementById(control.getAttribute('for')) || control.querySelector('input')
      : control.querySelector?.('input');
    const className = typeof control.className === 'string' ? control.className : '';

    const selectedDescendant = control.querySelector?.('[aria-selected="true"], [aria-pressed="true"], [data-selected="true"], .active, .selected, .checked');

    return control.getAttribute?.('aria-selected') === 'true'
      || control.getAttribute?.('aria-pressed') === 'true'
      || control.getAttribute?.('aria-current') === 'true'
      || control.dataset?.selected === 'true'
      || Boolean(input?.checked)
      || /(^|[\s_-])(active|selected|checked)([\s_-]|$)/i.test(className)
      || Boolean(selectedDescendant);
  };

  const backgroundLightness = element => {
    const control = interactiveControl(element);
    const match = window.getComputedStyle(control).backgroundColor.match(/[\d.]+/g);
    if (!match || Number(match[3]) === 0) return null;
    const [red, green, blue] = match.map(Number);
    return (red * 299 + green * 587 + blue * 114) / 1000;
  };

  const detectFulfillmentMethod = () => {
    const delivery = findTextControl(methodLabels.delivery);
    const pickup = findTextControl(methodLabels.pickup);
    if (!delivery && !pickup) return;

    if (isSelected(pickup)) fulfillmentMethod = 'pickup';
    else if (isSelected(delivery)) fulfillmentMethod = 'delivery';
    else {
      const savedMethod = sessionStorage.getItem('cart-fulfillment-method');
      if (savedMethod === 'pickup' || savedMethod === 'delivery') {
        fulfillmentMethod = savedMethod;
      } else {
        const deliveryLightness = backgroundLightness(delivery);
        const pickupLightness = backgroundLightness(pickup);
        fulfillmentMethod = deliveryLightness !== null
          && pickupLightness !== null
          && pickupLightness + 30 < deliveryLightness
          ? 'pickup'
          : 'delivery';
      }
    }
  };

  const formatMoney = cents => {
    const value = (Number(cents) / 100).toFixed(2);
    const format = config.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  };

  const updateMinimumMessage = blocked => {
    const remaining = Math.max(0, config.minimum - config.cartTotal);
    document.querySelectorAll('[data-delivery-minimum-message]').forEach(message => {
      message.hidden = !blocked;
      const minimum = message.querySelector('[data-delivery-minimum-amount]');
      const shortfall = message.querySelector('[data-delivery-minimum-shortfall]');
      if (minimum) minimum.textContent = formatMoney(config.minimum);
      if (shortfall) shortfall.textContent = formatMoney(remaining);
    });
  };

  const updateValidation = () => {
    const blocked = config.enabled
      && fulfillmentMethod === 'delivery'
      && config.cartTotal > 0
      && config.cartTotal < config.minimum;

    window.CartCheckoutValidation?.setReason(
      'delivery-minimum',
      blocked,
      `Delivery orders require a minimum of ${formatMoney(config.minimum)} before checkout.`
    );
    updateMinimumMessage(blocked);
  };

  const removeEstimate = () => {
    document.querySelectorAll('[data-pickeasy-delivery-estimate]').forEach(element => element.remove());
  };

  const clearEstimate = () => {
    rateRequest += 1;
    removeEstimate();
  };

  const findPostalInput = checkControl => {
    let container = checkControl;
    for (let depth = 0; container && depth < 7; depth++, container = container.parentElement) {
      const inputs = Array.from(container.querySelectorAll?.('input') || []).filter(input => {
        const signature = `${input.name} ${input.id} ${input.className} ${input.placeholder}`.toLowerCase();
        return isVisible(input) && input.type !== 'hidden' && !/date|time/.test(signature);
      });
      const postalInput = inputs.find(input => /zip|postal/.test(`${input.name} ${input.id} ${input.className} ${input.placeholder}`.toLowerCase()));
      if (postalInput) return postalInput;
      if (inputs.length === 1) return inputs[0];
    }
    return null;
  };

  const commonParent = (first, second) => {
    let parent = first?.parentElement;
    while (parent && !parent.contains(second)) parent = parent.parentElement;
    return parent;
  };

  const showEstimate = (input, checkControl, text, state = '') => {
    removeEstimate();
    const estimate = document.createElement('p');
    estimate.className = 'pickeasy-delivery-estimate text-size--small';
    estimate.dataset.pickeasyDeliveryEstimate = '';
    estimate.dataset.state = state;
    estimate.setAttribute('role', state === 'error' ? 'alert' : 'status');
    estimate.textContent = text;

    const row = commonParent(input, checkControl);
    if (row && row !== document.body && row.parentElement) row.insertAdjacentElement('afterend', estimate);
    else input.insertAdjacentElement('afterend', estimate);
  };

  const readResponse = async response => {
    const text = await response.text();
    if (!text) return null;
    try { return JSON.parse(text); }
    catch (error) { throw new Error(text); }
  };

  const calculateRate = async (postalCode, input, checkControl) => {
    if (!config.estimatorEnabled || fulfillmentMethod !== 'delivery') return;
    const request = ++rateRequest;
    showEstimate(input, checkControl, 'Calculating delivery charge…');

    const params = new URLSearchParams({
      'shipping_address[zip]': postalCode,
      'shipping_address[country]': 'Canada',
      'shipping_address[province]': 'Ontario'
    });
    const cartUrl = window.KROWN?.settings?.routes?.cart_url?.replace(/\/$/, '') || '/cart';

    try {
      const prepareResponse = await fetch(`${cartUrl}/prepare_shipping_rates.json?${params}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!prepareResponse.ok) throw (await readResponse(prepareResponse)) || new Error('Unable to calculate the delivery charge.');

      let result = null;
      for (let attempt = 0; attempt < 12 && result === null; attempt++) {
        if (attempt) await new Promise(resolve => setTimeout(resolve, 500));
        const ratesResponse = await fetch(`${cartUrl}/async_shipping_rates.json?${params}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store'
        });
        if (!ratesResponse.ok) throw (await readResponse(ratesResponse)) || new Error('Unable to retrieve the delivery charge.');
        result = await ratesResponse.json();
      }

      if (request !== rateRequest || fulfillmentMethod !== 'delivery') return;
      const rates = Array.isArray(result?.shipping_rates) ? result.shipping_rates : [];
      if (!rates.length) throw new Error('No delivery charge is available for this postal code.');

      const lowestRate = rates.reduce((lowest, rate) => {
        const amount = Math.round(Number.parseFloat(rate.price) * 100);
        return !lowest || amount < lowest.amount ? { rate, amount } : lowest;
      }, null);
      const rateName = lowestRate.rate.presentment_name || lowestRate.rate.name || 'Delivery';
      showEstimate(input, checkControl, `${rateName}: ${formatMoney(lowestRate.amount)}`);
    } catch (error) {
      if (request !== rateRequest || fulfillmentMethod !== 'delivery') return;
      const message = error instanceof Error ? error.message : 'Unable to calculate the delivery charge.';
      showEstimate(input, checkControl, message, 'error');
    }
  };

  const handleCheck = target => {
    const control = interactiveControl(target);
    if (normalizedText(control) !== 'check') return false;
    const input = findPostalInput(control);
    const postalCode = input?.value.trim();
    if (!input || !postalCode || fulfillmentMethod !== 'delivery') return true;

    lastPostalCode = postalCode;
    window.setTimeout(() => calculateRate(postalCode, input, control), 100);
    return true;
  };

  const showCheckoutWarning = () => {
    const messages = Array.from(document.querySelectorAll('[data-delivery-minimum-message]:not([hidden])'));
    const message = messages.find(element => element.offsetParent !== null) || messages[0];
    message?.focus({ preventScroll: true });
  };

  window.updateCartDeliveryValidation = (root = document) => {
    const marker = root.querySelector('[data-cart-delivery-validation]');
    if (marker) {
      config = {
        enabled: marker.dataset.enabled === 'true',
        estimatorEnabled: marker.dataset.estimatorEnabled === 'true',
        cartTotal: Number(marker.dataset.cartTotal) || 0,
        minimum: Number(marker.dataset.minimum) || 0,
        moneyFormat: marker.dataset.moneyFormat || '${{amount}}'
      };
    }
    detectFulfillmentMethod();
    updateValidation();
    if (lastPostalCode && fulfillmentMethod === 'delivery') clearEstimate();
  };

  document.addEventListener('click', event => {
    if (handleCheck(event.target)) return;

    const control = interactiveControl(event.target);
    const text = normalizedText(control);
    if (text === methodLabels.delivery || text === methodLabels.pickup) {
      fulfillmentMethod = text === methodLabels.delivery ? 'delivery' : 'pickup';
      sessionStorage.setItem('cart-fulfillment-method', fulfillmentMethod);
      if (fulfillmentMethod === 'pickup') clearEstimate();
      updateValidation();
      return;
    }

    const checkout = event.target.closest(checkoutSelector);
    if (!checkout || !window.CartCheckoutValidation?.hasReason('delivery-minimum')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showCheckoutWarning();
  }, true);

  document.addEventListener('submit', event => {
    if (!window.CartCheckoutValidation?.hasReason('delivery-minimum') || event.target.id !== 'cart') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showCheckoutWarning();
  }, true);

  const observer = new MutationObserver(() => {
    if (observerQueued) return;
    observerQueued = true;
    window.requestAnimationFrame(() => {
      observerQueued = false;
      const previousMethod = fulfillmentMethod;
      detectFulfillmentMethod();
      if (previousMethod !== fulfillmentMethod) {
        if (fulfillmentMethod === 'pickup') clearEstimate();
        updateValidation();
      }
    });
  });

  const init = () => {
    window.updateCartDeliveryValidation();
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'aria-pressed'] });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
